// src/pages/produtos/variacao/VariacaoCad.js
import React, { useEffect, useState } from 'react';
import { MdDelete } from "react-icons/md";
import { IoIosAddCircle } from "react-icons/io";
import Input from '../../../components/input/Input';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { useYesNo } from '../../../contexts/geral/YesNoContext';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import { useVariacao } from '../../../contexts/produtos/VariacaoContext';
import { useVariacaoItem } from '../../../contexts/produtos/VariacaoItemContext';
import { Variacao } from '../../../models/produtos/Variacao';
import { VariacaoItem } from '../../../models/produtos/VariacaoItem';
import VariacaoSearch from './VariacaoSearch';
import { normalizeDecimalInputBr, formatNumeric, parseNumeric } from '../../../utils/numberUtils';
import './VariacaoCad.css';
import ModalActions, { ModalActionButton } from '../../../components/modalForm/ModalActions';

const createEmptyItem = () => ({
    ...new VariacaoItem(),
    variacaoId: 0,
    isDefault: false,
    valorAdicional: 0
});

const createEmptyVariacao = () => new Variacao(0, '', [createEmptyItem()]);

const VariacaoCad = ({ show, onHide }) => {
    const { variacoes, addVariacao, updateVariacao, deleteVariacao } = useVariacao();
    const { fetchVariacaoItens, addVariacaoItem, updateVariacaoItem, deleteVariacaoItem } = useVariacaoItem();
    const [variacao, setVariacao] = useState(createEmptyVariacao());
    const [showSearchVariacao, setShowSearchVariacao] = useState(false);
    const [novoReg, setNovoReg] = useState(false);
    const [removedItemIds, setRemovedItemIds] = useState([]);
    const { showMessage } = useMessage();
    const { showYesNo } = useYesNo();

    useEffect(() => {
        if (show) handleNovo();
    }, [show]);

    const handleNovo = () => {
        setVariacao(createEmptyVariacao());
        setRemovedItemIds([]);
        setNovoReg(true);
    };

    const handleEditar = (id) => {
        if (id === 0) {
            showMessage('Selecione uma variacao para editar!');
            return;
        }

        setNovoReg(false);
    };

    const handleDelete = async () => {
        if (!variacao?.id) {
            showMessage('Selecione uma variacao para excluir!');
            return;
        }

        const resposta = await showYesNo('Deseja excluir a variacao selecionada?');
        if (resposta) {
            await deleteVariacao(variacao.id);
            handleNovo();
        }
    };

    const normalizeItens = (itens) => {
        const itensPreenchidos = (itens || [])
            .map((item) => ({
                ...item,
                descricao: item.descricao?.trim() || '',
                isDefault: !!item.isDefault,
                valorAdicional: parseNumeric(item.valorAdicional)
            }))
            .filter((item) => item.descricao);

        if (itensPreenchidos.length === 0) return [];

        const defaultIndex = itensPreenchidos.findIndex((item) => item.isDefault);

        return itensPreenchidos.map((item, index) => ({
            ...item,
            isDefault: defaultIndex >= 0 && index === defaultIndex
        }));
    };

    const loadVariacao = async (variacaoSel) => {
        if (!variacaoSel) {
            setVariacao(createEmptyVariacao());
            setRemovedItemIds([]);
            return;
        }

        const itens = await fetchVariacaoItens(variacaoSel.id);
        setVariacao({
            ...variacaoSel,
            variacaoItens: itens.length ? itens : [createEmptyItem()]
        });
        setRemovedItemIds([]);
    };

    const persistItens = async (variacaoId, itens) => {
        const itensPersistidos = [];

        for (const itemId of removedItemIds) {
            const deleted = await deleteVariacaoItem(itemId, { silent: true });
            if (!deleted) return null;
        }

        for (const item of itens) {
            const payload = {
                ...item,
                variacaoId
            };

            const result = item.id
                ? await updateVariacaoItem(payload, { silent: true })
                : await addVariacaoItem(payload, { silent: true });

            if (!result) return null;

            itensPersistidos.push(result);
        }

        return itensPersistidos;
    };

    const handleSave = async () => {
        if (!variacao.descricao?.trim()) {
            showMessage('Informe uma descricao para a variacao!');
            return;
        }

        if ((variacao.variacaoItens || []).some(item => parseNumeric(item.valorAdicional) < 0)) {
            showMessage('O valor adicional não pode ser negativo.', 'warning');
            return;
        }
        const itensNormalizados = normalizeItens(variacao.variacaoItens);
        if (itensNormalizados.length === 0) {
            showMessage('Adicione ao menos um item com descricao para a variacao!');
            return;
        }

        const variacaoPayload = {
            ...variacao,
            descricao: variacao.descricao.trim(),
            variacaoItens: []
        };

        const variacaoSalva = !variacao.id
            ? await addVariacao(variacaoPayload)
            : await updateVariacao(variacaoPayload);

        if (!variacaoSalva?.id) {
            return;
        }

        const itensPersistidos = await persistItens(variacaoSalva.id, itensNormalizados);
        if (!itensPersistidos) {
            return;
        }

        setRemovedItemIds([]);
        setVariacao({
            ...variacaoSalva,
            variacaoItens: itensPersistidos.length ? itensPersistidos : [createEmptyItem()]
        });
        handleNovo();
    };

    const handleAddItem = () => {
        setVariacao((current) => ({
            ...current,
            variacaoItens: [...(current.variacaoItens || []), createEmptyItem()]
        }));
    };

    const handleRemoveItem = (index) => {
        const updatedItems = [...(variacao.variacaoItens || [])];
        const [removedItem] = updatedItems.splice(index, 1);

        if (removedItem?.id) {
            setRemovedItemIds((current) => [...current, removedItem.id]);
        }

        setVariacao({
            ...variacao,
            variacaoItens: updatedItems.length > 0 ? updatedItems : [createEmptyItem()]
        });
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...(variacao.variacaoItens || [])];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };

        if (field === 'isDefault' && value) {
            updatedItems.forEach((item, idx) => {
                if (idx !== index) item.isDefault = false;
            });
        }

        setVariacao({ ...variacao, variacaoItens: updatedItems });
    };

    const handleExit = async () => {
        const variacaoSel = variacoes.find((v) => v.id.toString() === variacao?.id?.toString());
        if (!variacaoSel) {
            setVariacao(createEmptyVariacao());
            setRemovedItemIds([]);
            return;
        }

        await loadVariacao(variacaoSel);
        handleEditar(variacaoSel.id);
    };

    const handleSelectVariacao = async (variacaoSel) => {
        setShowSearchVariacao(false);

        if (!variacaoSel) {
            setVariacao(createEmptyVariacao());
            setRemovedItemIds([]);
            return;
        }

        await loadVariacao(variacaoSel);
        handleEditar(variacaoSel.id);
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura='600px' variant="cadastro" onHide={onHide}>
            <ModalHeader title={novoReg ? 'Nova Variacao' : 'Alterar Variacao'} onHide={onHide} />
            <ModalBody>
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="input-group mb-3">
                        <Input
                            label="Codigo"
                            value={variacao.id || ''}
                            onChange={(e) => setVariacao({ ...variacao, id: e.target.value })}
                            onBlur={handleExit}
                            disabled={!novoReg}
                            btnBuscar={() => setShowSearchVariacao(true)}
                        />
                        <Input
                            label="Descricao"
                            value={variacao.descricao || ''}
                            onChange={(e) => setVariacao({ ...variacao, descricao: e.target.value })}
                            size={300}
                            autoFocus
                        />
                    </div>

                    <h6>Itens da Variacao</h6>
                    <table className="table table-bordered align-middle variacao-cad-itens">
                        <thead>
                            <tr>
                                <th>Descricao</th>
                                <th className="text-center">Valor</th>
                                <th className="text-center">Padrao</th>
                                <th className="text-center">Acoes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(variacao.variacaoItens || []).map((item, idx) => (
                                <tr key={item.id || `novo-${idx}`}>
                                    <td>
                                        <input
                                            className="form-control form-input"
                                            value={item.descricao || ''}
                                            onChange={(e) => handleItemChange(idx, 'descricao', e.target.value)}
                                            placeholder="Informe a descricao do item"
                                        />
                                    </td>
                                    <td>
                                        <Input type="text" value={item.valorAdicional ?? 0} size={100} style={{ textAlign: 'right' }}
                                            onChange={e => handleItemChange(idx, 'valorAdicional', normalizeDecimalInputBr(e.target.value))}
                                            onBlur={() => handleItemChange(idx, 'valorAdicional', formatNumeric(item.valorAdicional, 2))} />
                                    </td>
                                    <td className="text-center">
                                        <input
                                            type="checkbox"
                                            checked={item.isDefault || false}
                                            onChange={(e) => handleItemChange(idx, 'isDefault', e.target.checked)}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            className="btn btn-danger btn-sm variacao-cad-remover"
                                            type="button"
                                            onClick={() => handleRemoveItem(idx)}
                                        >
                                            Remover <MdDelete />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button className="btn btn-outline-primary btn-sm" type="button" onClick={handleAddItem}>
                        Adicionar Item <IoIosAddCircle />
                    </button>
                </form>
            </ModalBody>
            <ModalFooter>
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" type="button" onClick={handleSave} action="save">Salvar </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" type="button" onClick={handleDelete} disabled={novoReg} action="delete">Excluir </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" type="button" onClick={handleNovo} disabled={novoReg} action="new">Novo </ModalActionButton>
                </ModalActions>
            </ModalFooter>

            <VariacaoSearch
                show={showSearchVariacao}
                onHide={() => setShowSearchVariacao(false)}
                onSelectVariacao={handleSelectVariacao}
            />
        </ModalContainer>
    );
};

export default VariacaoCad;
